import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import Flow from './flow/App';

const FlowEditor = ({ open, onClose, flow, onSave }) => {
  const [currentFlow, setCurrentFlow] = useState(flow);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const flowRef = useRef(null);
  const [activeLanguage, setActiveLanguage] = useState('ru');

  useEffect(() => {
    setCurrentFlow(flow);
  }, [flow]);

  const handleNotification = (notification) => {
    if (notification.status === 'COMPLETED') {
      setSnackbar({ open: true, message: 'Flow saved successfully.', severity: 'success' });
    } else if (notification.status === 'FAILED') {
      setSnackbar({ open: true, message: 'Failed to save flow', severity: 'error' });
    } else if (notification.status === 'PENDING') {
      setSnackbar({ open: true, message: 'Saving flow…', severity: 'info' });
    }
  };

  const handleSave = () => {
    console.log('FlowEditor handleSave called');
    if (flowRef.current && flowRef.current.handleSave) {
      setIsSaving(true);
      setSnackbar({ open: true, message: 'Saving flow…', severity: 'info' });
      flowRef.current.handleSave();
      // Reset saving state after 2s
      setTimeout(() => {
        setIsSaving(false);
      }, 2000);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!currentFlow) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="100vw" 
      fullWidth 
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          margin: 0,
          borderRadius: 0
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={activeLanguage}
            exclusive
            size="small"
            onChange={(e, lang) => { if (lang) setActiveLanguage(lang); }}
          >
            <ToggleButton value="ru">RU</ToggleButton>
            <ToggleButton value="en">EN</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="h6">
            Editing flow: {currentFlow.name}
          </Typography>
        </Box>
        <Box>
          <IconButton 
            onClick={handleSave} 
            color="primary" 
            title={isSaving ? 'Saving…' : 'Save'}
            disabled={isSaving}
          >
            <Save sx={{ 
              animation: isSaving ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }} />
          </IconButton>
          <IconButton onClick={handleClose} color="inherit">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, height: 'calc(100vh - 64px)' }}>
        <Box sx={{ height: '100%' }}>
          <Flow 
            ref={flowRef}
            flowId={currentFlow._id}
            flowName={currentFlow.name}
            activeLanguage={activeLanguage}
            onSave={handleSave}
            onClose={handleClose}
            onNotification={handleNotification}
          />
        </Box>
      </DialogContent>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default FlowEditor; 